'use client';

import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

const sponsors2025 = [
  { name: 'Saffola SOYA', logo: '/assets/images/partners/saffola.png' },
  { name: 'ICICI Bank', logo: '/assets/images/partners/icici-bank.png' },
  { name: 'ICICI Lombard', logo: '/assets/images/partners/icici-lombard.png' },
  { name: 'RBL Bank', logo: '/assets/images/partners/rbl-bank.png' },
  { name: "Ching's Secret", logo: '/assets/images/partners/chings.png' },
  { name: 'HDFC Ergo', logo: '/assets/images/partners/hdfc-ergo.png' },
  { name: 'Birla Opus Paints', logo: '/assets/images/partners/birla-opus-paints.png' },
  { name: 'Mirae Asset MF', logo: '/assets/images/partners/mirae-asset.png' },
  { name: 'India Today', logo: '/assets/images/partners/india-today.png' },
  { name: 'Godrej Cinthol', logo: '/assets/images/partners/cinthol.png' },
  { name: 'Campa', logo: '/assets/images/partners/campa.png' },
  { name: 'Aaj Tak', logo: '/assets/images/partners/aaj-tak.png' },
  { name: 'TV9 Bharatvarsh', logo: '/assets/images/partners/tv9-bharatvarsh.webp' },
  { name: 'Independence', logo: '/assets/images/partners/independence.png' },
  { name: 'Star Sports', logo: '/assets/images/partners/star-sports.png' },
  { name: 'Star Jalsha', logo: '/assets/images/partners/star-jalsha.png' },
  { name: 'Colors Bangla Cinema', logo: '/assets/images/partners/colors-bangla-cinema.png' },
  { name: 'Lotus', logo: '/assets/images/partners/lotus.png' },
  { name: 'Godrej aer', logo: '/assets/images/partners/godrej-aer.png' },
  { name: 'SBI Life', logo: '/assets/images/partners/sbi-life.png' },
  { name: 'Agrawals', logo: '/assets/images/partners/agarwals.jpg' },
  { name: 'TATA AIG', logo: '/assets/images/partners/tata-aig.png' },
  { name: 'Fortune', logo: '/assets/images/partners/fortune.png' },
  { name: 'Mother Dairy', logo: '/assets/images/partners/mother-dairy.png' },
];

const mediaQuotes = [
  {
    outlet: 'India Today',
    quote: 'HCAF has reimagined what a community Durga Puja can be - a genuine cultural movement in the heart of Mumbai.',
    type: 'Print & Digital',
  },
  {
    outlet: 'Aaj Tak',
    quote: 'An authentic celebration that brings together Mumbai\'s diverse communities under one roof.',
    type: 'Broadcast',
  },
  {
    outlet: 'TV9 Bharatvarsh',
    quote: 'From humble beginnings to 10,000+ footfall - HCAF\'s Puja has become a landmark Mumbai event.',
    type: 'Broadcast',
  },
];

export default function SocialProofSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('active'); });
      },
      { threshold: 0.08 }
    );
    sectionRef?.current?.querySelectorAll('.reveal')?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="bg-dark-bg noise-overlay w-full px-4 md:px-12 pt-14 md:pt-20 pb-12 md:pb-16">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10 md:mb-14 reveal">
          <span className="inline-block px-4 py-1.5 rounded-full border border-accent/30 text-accent text-xs font-bold tracking-widest uppercase mb-4 md:mb-5">
            Trusted & Covered
          </span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tight">
            Brands & Media That <span className="text-accent">Trust HCAF</span>
          </h2>
        </div>

        {/* Sponsor Logos */}
        <div className="mb-10 md:mb-14 reveal">
          <p className="text-white/40 text-xs font-bold tracking-widest uppercase text-center mb-6 md:mb-8">2025 Partners</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 md:gap-4">
            {sponsors2025?.map((sponsor) => (
              <div key={sponsor.name} className="sponsor-logo-box rounded-2xl">
                <div className="relative w-full h-20 md:h-24">
                  <AppImage
                    src={sponsor.logo}
                    alt={`${sponsor.name} logo`}
                    fill
                    className="object-contain"
                    style={{ objectFit: 'contain' }}
                    sizes="200px" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Media Quotes */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {mediaQuotes?.map((q, i) => (
            <div
              key={q?.outlet}
              className="reveal bg-dark-card border border-accent/15 rounded-2xl md:rounded-3xl p-5 md:p-7"
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div className="flex items-center gap-3 mb-4 md:mb-5">
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary flex-shrink-0">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z"/><path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z"/>
                  </svg>
                </div>
                <div>
                  <p className="text-white font-bold text-sm">{q?.outlet}</p>
                  <p className="text-white/40 text-xs">{q?.type}</p>
                </div>
              </div>
              <p className="text-white/70 text-sm leading-relaxed italic">&ldquo;{q?.quote}&rdquo;</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}